import React, { useMemo } from 'react';
import { CompleteKundali, Language } from '../../types/kundali';
import { 
  Calendar, 
  Clock, 
  RotateCcw, 
  ShieldCheck, 
  AlertTriangle, 
  SlidersHorizontal, 
  Sparkles, 
  Compass, 
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  User
} from 'lucide-react';
import { ZODIAC_HI } from '../../utils/language';

interface CockpitHeaderProps {
  kundali: CompleteKundali;
  lang?: Language;
  targetAnalysisDateTime: Date;
  onTargetAnalysisDateTimeChange: (newDate: Date) => void;
  onOpenBtrDrawer?: () => void;
}

export const CockpitHeader: React.FC<CockpitHeaderProps> = ({
  kundali,
  lang = 'en',
  targetAnalysisDateTime,
  onTargetAnalysisDateTimeChange,
  onOpenBtrDrawer
}) => {
  const isHi = lang === 'hi';
  const { birthDetails } = kundali;

  // Format date helper: YYYY-MM-DD
  const dateInputValue = useMemo(() => {
    const y = targetAnalysisDateTime.getFullYear();
    const m = String(targetAnalysisDateTime.getMonth() + 1).padStart(2, '0');
    const d = String(targetAnalysisDateTime.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [targetAnalysisDateTime]);

  // Format time helper: HH:mm
  const timeInputValue = useMemo(() => {
    const hrs = String(targetAnalysisDateTime.getHours()).padStart(2, '0');
    const mins = String(targetAnalysisDateTime.getMinutes()).padStart(2, '0');
    return `${hrs}:${mins}`;
  }, [targetAnalysisDateTime]);

  // Readable human format
  const formattedAnalysisMoment = useMemo(() => {
    const d = targetAnalysisDateTime;
    const day = d.getDate().toString().padStart(2, '0');
    const monthsEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthsHi = ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'];
    const month = isHi ? monthsHi[d.getMonth()] : monthsEn[d.getMonth()];
    const year = d.getFullYear();
    const hrs = d.getHours();
    const mins = d.getMinutes().toString().padStart(2, '0');
    const ampm = hrs >= 12 ? 'PM' : 'AM';
    const hrs12 = hrs % 12 || 12;
    return `${day} ${month} ${year}, ${hrs12.toString().padStart(2, '0')}:${mins} ${ampm} IST`;
  }, [targetAnalysisDateTime, isHi]);

  // Handle date input change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const [y, m, d] = val.split('-').map(Number);
    if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
      const updated = new Date(targetAnalysisDateTime);
      updated.setFullYear(y, m - 1, d);
      onTargetAnalysisDateTimeChange(updated);
    }
  };

  // Handle time input change
  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) return;
    const [hrs, mins] = val.split(':').map(Number);
    if (!isNaN(hrs) && !isNaN(mins)) {
      const updated = new Date(targetAnalysisDateTime);
      updated.setHours(hrs, mins, 0, 0);
      onTargetAnalysisDateTimeChange(updated);
    }
  };

  // Quick jump modifiers
  const handleQuickJump = (type: 'now' | 'birth' | 'prevMonth' | 'nextMonth' | 'prevYear' | 'nextYear') => {
    if (type === 'now') {
      onTargetAnalysisDateTimeChange(new Date());
    } else if (type === 'birth') {
      if (birthDetails?.date) {
        const timePart = birthDetails.time || '12:00:00';
        const [y, m, d] = birthDetails.date.split('-').map(Number);
        const [hh, mm, ss] = timePart.split(':').map(Number);
        const birthDate = new Date(y, m - 1, d, hh || 12, mm || 0, ss || 0);
        onTargetAnalysisDateTimeChange(birthDate);
      }
    } else if (type === 'prevMonth') {
      const d = new Date(targetAnalysisDateTime);
      d.setMonth(d.getMonth() - 1);
      onTargetAnalysisDateTimeChange(d);
    } else if (type === 'nextMonth') {
      const d = new Date(targetAnalysisDateTime);
      d.setMonth(d.getMonth() + 1);
      onTargetAnalysisDateTimeChange(d);
    } else if (type === 'prevYear') {
      const d = new Date(targetAnalysisDateTime);
      d.setFullYear(d.getFullYear() - 1);
      onTargetAnalysisDateTimeChange(d);
    } else if (type === 'nextYear') {
      const d = new Date(targetAnalysisDateTime);
      d.setFullYear(d.getFullYear() + 1);
      onTargetAnalysisDateTimeChange(d);
    }
  };

  // BTR Status details
  const btrStatus = birthDetails?.btrStatus || 'unverified';
  const deltaSeconds = birthDetails?.rectifiedDeltaSeconds || 0;
  const isBtrVerified = btrStatus === 'rectified' || btrStatus === 'verified';

  // Lagna & Moon
  const lagnaHouse = kundali.houses[0];
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const moonPlanet = kundali.planets.find(p => p.name === 'Moon');
  const moonSign = moonPlanet?.sign || 'Aries';
  const moonNakshatra = moonPlanet?.nakshatra || '';

  return (
    <div className="bg-[#FFFDF7] border border-[#B58A4A]/30 rounded-2xl shadow-sm p-4 mb-5 text-[#174C4B]">
      {/* Top row: Profile identification & BTR bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-[#B58A4A]/20">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#174C4B]/10 border border-[#174C4B]/20 flex items-center justify-center text-[#174C4B] font-serif font-bold text-lg">
            {birthDetails?.name ? birthDetails.name.charAt(0).toUpperCase() : 'N'}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-serif font-bold text-lg text-[#174C4B] tracking-tight">
                {birthDetails?.name || (isHi ? 'अज्ञात जातक' : 'Native Kundali')}
              </h1>
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#174C4B]/5 text-[#174C4B] font-medium border border-[#174C4B]/15">
                {isHi ? (ZODIAC_HI[lagnaSign as keyof typeof ZODIAC_HI] || lagnaSign) : lagnaSign} Lagna
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#B58A4A]/10 text-[#8E692B] font-medium border border-[#B58A4A]/25">
                {isHi ? (ZODIAC_HI[moonSign as keyof typeof ZODIAC_HI] || moonSign) : moonSign} Rashi ({moonNakshatra})
              </span>
            </div>
            <div className="text-xs text-stone-500 flex items-center gap-2 mt-0.5">
              <span>{birthDetails?.date} {birthDetails?.time}</span>
              <span>•</span>
              <span className="truncate max-w-[200px]">{birthDetails?.placeName || 'Unknown Location'}</span>
            </div>
          </div>
        </div>

        {/* BTR Status & Calibrate Button */}
        <div className="flex items-center gap-2">
          {isBtrVerified ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-300 text-xs font-medium">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
              <span>{isHi ? 'BTR सत्यापित' : 'BTR Calibrated'} {deltaSeconds !== 0 && `(Δ ${deltaSeconds > 0 ? '+' : ''}${deltaSeconds}s)`}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-300 text-xs font-medium">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              <span>{isHi ? 'BTR असंपादित (सूक्ष्म-दशा संवेदनशील)' : 'BTR Uncalibrated (Micro-Dasha Sensitive)'}</span>
            </div>
          )}

          {onOpenBtrDrawer && (
            <button
              onClick={onOpenBtrDrawer}
              className="px-2.5 py-1 rounded-lg bg-[#174C4B] hover:bg-[#123837] text-white text-xs font-medium flex items-center gap-1.5 transition-colors shadow-xs"
              title="Open Birth Time Rectification (BTR) Drawer"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span>{isHi ? 'BTR टूल' : 'BTR Calibrate'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Bottom row: Authoritative Shared Analytical Moment Controller */}
      <div className="pt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-[#174C4B] uppercase tracking-wider">
            <Clock className="w-4 h-4 text-[#B58A4A]" />
            <span>{isHi ? 'विश्लेषण क्षण (Shared Moment):' : 'Shared Analysis Moment:'}</span>
          </div>
          <span className="text-sm font-semibold font-mono text-[#174C4B] px-2.5 py-0.5 rounded-md bg-[#174C4B]/5 border border-[#174C4B]/15">
            {formattedAnalysisMoment}
          </span>
        </div>

        {/* Temporal Navigation & Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-[#B58A4A]/40 rounded-lg p-1 shadow-2xs">
            <Calendar className="w-3.5 h-3.5 text-stone-400 ml-1" />
            <input
              type="date"
              value={dateInputValue}
              onChange={handleDateChange}
              className="text-xs bg-transparent text-stone-800 font-medium px-1.5 py-0.5 focus:outline-none"
              title="Target Analysis Date"
            />
            <span className="text-stone-300">|</span>
            <input
              type="time"
              value={timeInputValue}
              onChange={handleTimeChange}
              className="text-xs bg-transparent text-stone-800 font-medium px-1.5 py-0.5 focus:outline-none"
              title="Target Analysis Time"
            />
          </div>

          {/* Quick jump actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleQuickJump('prevYear')}
              className="px-2 py-1 rounded-md bg-stone-100 hover:bg-[#B58A4A]/20 text-stone-700 hover:text-[#174C4B] text-xs font-medium transition-colors border border-stone-200"
              title="Subtract 1 Year"
            >
              -1Y
            </button>
            <button
              onClick={() => handleQuickJump('prevMonth')}
              className="px-2 py-1 rounded-md bg-stone-100 hover:bg-[#B58A4A]/20 text-stone-700 hover:text-[#174C4B] text-xs font-medium transition-colors border border-stone-200"
              title="Subtract 1 Month"
            >
              -1M
            </button>
            <button
              onClick={() => handleQuickJump('now')}
              className="px-2.5 py-1 rounded-md bg-[#B58A4A]/15 hover:bg-[#B58A4A]/30 text-[#8E692B] text-xs font-semibold transition-colors border border-[#B58A4A]/40"
              title="Jump to Current Live Moment"
            >
              {isHi ? 'वर्तमान (Now)' : 'Now'}
            </button>
            <button
              onClick={() => handleQuickJump('birth')}
              className="px-2.5 py-1 rounded-md bg-[#174C4B]/10 hover:bg-[#174C4B]/20 text-[#174C4B] text-xs font-semibold transition-colors border border-[#174C4B]/20"
              title="Jump to Birth Timestamp"
            >
              {isHi ? 'जन्म क्षण' : 'Birth'}
            </button>
            <button
              onClick={() => handleQuickJump('nextMonth')}
              className="px-2 py-1 rounded-md bg-stone-100 hover:bg-[#B58A4A]/20 text-stone-700 hover:text-[#174C4B] text-xs font-medium transition-colors border border-stone-200"
              title="Add 1 Month"
            >
              +1M
            </button>
            <button
              onClick={() => handleQuickJump('nextYear')}
              className="px-2 py-1 rounded-md bg-stone-100 hover:bg-[#B58A4A]/20 text-stone-700 hover:text-[#174C4B] text-xs font-medium transition-colors border border-stone-200"
              title="Add 1 Year"
            >
              +1Y
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
